import { Link, useParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import SeoHead from '@/components/feature/SeoHead';
import JsonLd from '@/components/feature/JsonLd';
import PageHero from '@/components/feature/PageHero';
import ActivityCard from '@/components/feature/ActivityCard';
import { activities } from '@/mocks/activities';
import { siteInfo } from '@/mocks/site';

interface DetailBlock {
  title: string;
  icon: string;
  paragraphs?: string[];
  list?: string[];
  ordered?: boolean;
}

const CONDS = [
  '完成基础账号注册并绑定常用手机号，用于奖励发放与身份核验。',
  '同一账号、同一设备在活动周期内按规则参与，异常多账号行为会被系统拦截。',
  '部分涉及实物奖励的活动需要填写收货信息，信息提交后不可自行修改。',
  '请勿使用任何自动化工具刷取任务，否则可能被取消资格并回收已发放奖励。',
];

const TASK_STEPS = [
  '进入活动详情页，确认当前活动的任务要求与有效期限。',
  '按照页面提示完成任务动作，系统会在完成后自动记录进度。',
  '在「我的福利」中查看任务状态，确认是「已完成」还是「待审核」。',
  '达到奖励门槛后提交领取，奖励将按活动说明的时间发放到账。',
];

const NOTES = [
  '奖励数量以活动页面实时展示为准，页面标注的数值仅供参考。',
  '活动期间规则如有调整，会提前在活动详情页更新并标注变更时间。',
  '奖励一经发放不予退回，若发现异常请通过反馈邮箱联系我们核实。',
  '活动全程免费，任何以「缴费才能领奖」为由的要求都与本站无关。',
];

function buildBlocks(reward: string, deadline: string, difficulty: string, tags: string[]): DetailBlock[] {
  return [
    {
      title: '参与条件',
      icon: 'ri-checkbox-multiple-line',
      list: CONDS,
    },
    {
      title: '任务明细与完成方式',
      icon: 'ri-list-check-2',
      ordered: true,
      list: TASK_STEPS,
    },
    {
      title: '奖励内容与发放时间',
      icon: 'ri-hand-coin-line',
      paragraphs: [
        `本活动的奖励内容为「${reward}」，参与难度属于「${difficulty}」级别，有效时间以活动页面标注的「${deadline}」为准。`,
        `奖励发放时间会因任务类型不同而有所差异：${tags.join('、')} 类任务通常在完成后按活动说明的时间到账，涉及人工审核的部分会稍晚一些。`,
        '你可以在「我的福利」页面查看每一笔奖励的状态，若显示「待发放」说明仍在处理中，显示「已到账」即可正常使用。',
      ],
    },
    {
      title: '注意事项',
      icon: 'ri-error-warning-line',
      list: NOTES,
    },
  ];
}

export default function ActivityDetail() {
  const { id } = useParams<{ id: string }>();
  const activity = activities.find((item) => item.id === id);

  if (!activity) {
    return (
      <>
        <SeoHead
          path="/activities"
          title="活动不存在 - 爆瓜福利"
          description="你访问的福利活动可能已结束或链接有误，请返回福利活动中心查看正在进行的活动。"
        />
        <Layout>
          <section className="w-full pt-32 md:pt-40 pb-20 px-4 md:px-8 bg-background-50">
            <div className="max-w-lg mx-auto text-center">
              <span className="inline-flex w-16 h-16 rounded-full bg-background-100 text-foreground-600 items-center justify-center text-3xl">
                <i className="ri-emotion-unhappy-line"></i>
              </span>
              <h1 className="mt-5 text-2xl md:text-3xl text-foreground-950">这个活动找不到了</h1>
              <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                活动可能已经结束或链接有误，回到福利活动中心看看正在进行的活动吧。
              </p>
              <Link
                to="/activities"
                className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                返回活动中心
              </Link>
            </div>
          </section>
        </Layout>
      </>
    );
  }

  const blocks = buildBlocks(activity.reward, activity.deadline, activity.difficulty, activity.tags);
  const related = activities.filter((item) => item.category === activity.category && item.id !== activity.id).slice(0, 3);

  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        name: `${activity.title} - 爆瓜福利活动规则说明`,
        description: activity.summary,
        url: `https://ikinxod.cn/activities/${activity.id}`,
        inLanguage: 'zh-CN',
        dateModified: new Date().toISOString().slice(0, 10),
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://ikinxod.cn/' },
          { '@type': 'ListItem', position: 2, name: '福利活动', item: 'https://ikinxod.cn/activities' },
          {
            '@type': 'ListItem',
            position: 3,
            name: activity.title,
            item: `https://ikinxod.cn/activities/${activity.id}`,
          },
        ],
      },
    ],
  };

  return (
    <>
      <SeoHead
        path={`/activities/${activity.id}`}
        title={`${activity.title} - 爆瓜福利活动规则与奖励说明`}
        description={`${activity.summary} 奖励：${activity.reward}，参与难度：${activity.difficulty}，有效期：${activity.deadline}。`}
        keywords={`${activity.title},爆瓜福利活动,${activity.tags.join(',')},活动规则,奖励说明`}
      />
      <JsonLd data={structuredData} />

      <Layout>
        <PageHero
          icon="ri-file-list-3-line"
          eyebrow="活动详情"
          title={activity.title}
          desc={activity.summary}
          crumbs={[
            { label: '首页', path: '/' },
            { label: '福利活动', path: '/activities' },
            { label: activity.title },
          ]}
        />

        <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-50">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
            <article className="lg:col-span-8">
              <div className="w-full h-56 md:h-80 rounded-2xl overflow-hidden bg-background-100 border border-background-200">
                <img
                  src={activity.image}
                  alt={`${activity.title} 活动示意图`}
                  title={`${activity.title} - 爆瓜福利活动`}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-2">
                {activity.hot && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs">
                    <i className="ri-fire-line"></i>
                    热门活动
                  </span>
                )}
                {activity.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full bg-background-100 text-foreground-700 text-xs">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="mt-8 flex flex-col gap-8">
                {blocks.map((block) => (
                  <section key={block.title} id={block.title === '注意事项' ? 'notes' : undefined} className="scroll-mt-24">
                    <h2 className="flex items-center gap-2.5 text-lg md:text-xl text-foreground-950">
                      <span className="w-9 h-9 rounded-xl bg-secondary-100 text-secondary-800 flex items-center justify-center text-lg">
                        <i className={block.icon}></i>
                      </span>
                      {block.title}
                    </h2>

                    {block.paragraphs?.map((p) => (
                      <p key={p} className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                        {p}
                      </p>
                    ))}

                    {block.list && (
                      <ul className="mt-4 flex flex-col gap-3">
                        {block.list.map((item, index) => (
                          <li key={item} className="flex items-start gap-3 bg-background-100/70 border border-background-200 rounded-xl p-4">
                            <span
                              className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                                block.ordered ? 'bg-primary-500 text-background-50' : 'bg-secondary-100 text-secondary-800'
                              }`}
                            >
                              {block.ordered ? index + 1 : <i className="ri-check-line"></i>}
                            </span>
                            <span className="text-sm text-foreground-700 leading-relaxed">{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>
                ))}
              </div>

              <div className="mt-8 bg-accent-50 border border-accent-200 rounded-2xl p-5 md:p-6">
                <h3 className="flex items-center gap-2 text-base text-accent-900">
                  <i className="ri-information-line"></i>
                  规则更新时间
                </h3>
                <p className="mt-2 text-sm text-accent-900/80 leading-relaxed">
                  本文档随活动规则同步维护，最后更新于 {new Date().toLocaleDateString('zh-CN')}。若与活动页面展示不一致，以活动页面为准。{siteInfo.updateNote}。
                </p>
              </div>
            </article>

            <aside className="lg:col-span-4">
              <div className="flex flex-col gap-5 lg:sticky lg:top-28">
                <div className="bg-background-100 border border-background-200 rounded-2xl p-5 md:p-6">
                  <h2 className="text-base text-foreground-950">活动速览</h2>
                  <dl className="mt-4 flex flex-col divide-y divide-background-200">
                    <div className="flex items-center justify-between gap-4 py-3">
                      <dt className="text-sm text-foreground-500">奖励内容</dt>
                      <dd className="text-sm text-primary-700 font-medium text-right">{activity.reward}</dd>
                    </div>
                    <div className="flex items-center justify-between gap-4 py-3">
                      <dt className="text-sm text-foreground-500">参与难度</dt>
                      <dd className="text-sm text-foreground-900 text-right">{activity.difficulty}</dd>
                    </div>
                    <div className="flex items-center justify-between gap-4 py-3">
                      <dt className="text-sm text-foreground-500">有效时间</dt>
                      <dd className="text-sm text-foreground-900 text-right">{activity.deadline}</dd>
                    </div>
                    <div className="flex items-center justify-between gap-4 py-3">
                      <dt className="text-sm text-foreground-500">参与人数</dt>
                      <dd className="text-sm text-foreground-900 text-right">{activity.participants}</dd>
                    </div>
                  </dl>

                  <div className="mt-5 flex flex-col gap-3">
                    <Link
                      to="/rules#claim"
                      className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                    >
                      <i className="ri-book-open-line"></i>
                      查看领取流程
                    </Link>
                    <Link
                      to={`/activities?cat=${activity.category}`}
                      className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-background-100 text-foreground-800 text-sm font-medium whitespace-nowrap hover:bg-background-200 transition-colors cursor-pointer"
                    >
                      <i className="ri-filter-3-line"></i>
                      找同类活动
                    </Link>
                  </div>
                </div>

                <div className="bg-secondary-50 border border-secondary-200 rounded-2xl p-5 md:p-6">
                  <h3 className="flex items-center gap-2 text-base text-secondary-900">
                    <i className="ri-shield-check-line"></i>
                    安全提醒
                  </h3>
                  <p className="mt-2 text-sm text-secondary-900/80 leading-relaxed">
                    本站活动全程免费，工作人员不会索要验证码或要求转账。遇到可疑情况请通过 {siteInfo.email} 与我们核实。
                  </p>
                </div>
              </div>
            </aside>
          </div>

          {related.length > 0 && (
            <section className="mt-14 md:mt-20">
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div>
                  <h2 className="text-xl md:text-2xl text-foreground-950">同类活动推荐</h2>
                  <p className="mt-2 text-sm text-foreground-600">玩法相近，可以一起做，奖励互不冲突。</p>
                </div>
                <Link
                  to={`/activities?cat=${activity.category}`}
                  className="self-start sm:self-auto inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  查看该分类全部活动
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {related.map((item) => (
                  <ActivityCard key={item.id} activity={item} />
                ))}
              </div>
            </section>
          )}
        </section>
      </Layout>
    </>
  );
}